import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Import locale files
import enCommon from "@/locales/en/common.json";
import esCommon from "@/locales/es/common.json";

/**
 * The passenger's OWN language choice (the language selector). Only an
 * explicit pick is stored here. The old "rutalink_language" key also cached
 * whatever the browser reported, so a choice and a phone setting looked the
 * same and the tenant's language could never win; it is no longer read.
 */
const LANGUAGE_CHOICE_KEY = "rutalink_language_choice";
const SUPPORTED_LANGUAGES = ["en", "es"] as const;

// This visit's choice, kept in memory too, so a pick still beats the tenant
// language when storage is blocked (private mode, blocked site data).
let sessionChoice: string | null = null;

/** The passenger's explicit language choice, if they ever made one. */
export function getSavedLanguage(): string | null {
  if (sessionChoice) return sessionChoice;
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(LANGUAGE_CHOICE_KEY);
  } catch {
    return null;
  }
}

/** The passenger picked a language: remember it and switch to it. */
export function chooseLanguage(lang: string): void {
  sessionChoice = lang;
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(LANGUAGE_CHOICE_KEY, lang);
    } catch {
      // storage blocked: sessionChoice still holds it for this visit
    }
  }
  i18n.changeLanguage(lang);
}

/** The browser's language, narrowed to one the site has. */
function browserLanguage(): string {
  const nav =
    typeof navigator !== "undefined" ? (navigator.language ?? "") : "";
  const base = nav.slice(0, 2).toLowerCase();
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? base : "en";
}

/**
 * The tenant's language (tenants.default_language) applies unless the
 * passenger chose one; a tenant without one gets the browser's language, so
 * moving between tenants in one visit never keeps the previous tenant's.
 * Pegasos riders get Spanish on an English phone.
 */
export function applyTenantLanguage(lang: string | null | undefined): void {
  if (getSavedLanguage()) return;
  const target = lang || browserLanguage();
  if (i18n.language !== target) i18n.changeLanguage(target);
}

// Define resources inline to avoid async loading issues
const resources = {
  en: {
    common: enCommon,
  },
  es: {
    common: esCommon,
  },
};

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    defaultNS: "common",
    ns: ["common"],

    // Language detection: the passenger's own choice, else the browser until
    // the tenant config arrives (applyTenantLanguage). Nothing is cached, so a
    // detected language is never mistaken for a choice.
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: LANGUAGE_CHOICE_KEY,
      caches: [],
    },

    interpolation: {
      // React already escapes values
      escapeValue: false,
    },

    // React-specific options
    react: {
      useSuspense: false, // Disable suspense for better compatibility
    },
  });

export default i18n;
